import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notificationsApi } from '../lib/api'
import { useAuth } from '../context/AuthContext'

interface Notification {
  id: string
  type: string
  title: string
  message: string
  actionUrl?: string | null
}

const DISMISSED_KEY = 'dismissedNotificationIds'
// Capped so this can't grow forever in a long-lived browser profile -- only the most
// recent dismissals matter for "don't pop this back up," not a full history.
const MAX_DISMISSED = 100

function getDismissedIds(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function addDismissedId(id: string) {
  const ids = getDismissedIds()
  if (ids.includes(id)) return
  const next = [...ids, id].slice(-MAX_DISMISSED)
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
  } catch {
    // ignore -- worst case a dismissed toast reappears once
  }
}

// Stays visible until the user actually does something with it -- marks it read (View
// or a shortcut action) or explicitly dismisses it (Cancel / the X) -- rather than
// disappearing on its own. That includes surviving a page change or a refresh: this
// used to be pure in-memory state driven only by the 'notification:new' event, so
// navigating away or reloading silently lost a still-pending notification even though
// it was still sitting there unread. Now it also checks on mount for the latest
// already-unread notification (unless the user already dismissed that specific one --
// tracked in localStorage so a dismissal sticks across reloads too) so it comes right
// back where you left off.
export default function NotificationToast() {
  const [notification, setNotification] = useState<Notification | null>(null)
  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    const handleNew = (e: Event) => {
      const detail = (e as CustomEvent<Notification>).detail
      if (detail && !getDismissedIds().includes(detail.id)) setNotification(detail)
    }
    window.addEventListener('notification:new', handleNew)
    return () => window.removeEventListener('notification:new', handleNew)
  }, [])

  useEffect(() => {
    // Gated on `user` (not just mount) -- this component renders unconditionally at
    // the app root, unlike NotificationBell which only mounts once Navbar has a
    // confirmed `user`. Firing this fetch before AuthContext's own initial
    // fetchUser() resolves raced the token/auth state on a fresh load and produced a
    // real, reproducible 401 -> token wipe -> forced logout. Waiting for `user` means
    // this only ever runs once we know the session is actually established.
    if (!user) return

    // On mount (first load, refresh, or coming back from a full page navigation),
    // check whether there's already an unread notification waiting that hasn't been
    // explicitly dismissed -- the polling-based 'notification:new' event only fires
    // for arrivals during this session, so without this a pre-existing unread
    // notification would silently never show.
    notificationsApi.getAll({ page: 1, limit: 1, status: 'UNREAD' })
      .then((res) => {
        const latest = res.data.success ? res.data.data?.[0] : null
        if (latest && !getDismissedIds().includes(latest.id)) setNotification(latest)
      })
      .catch(() => {
        // ignore -- this is a nice-to-have recovery check, not critical path
      })
  }, [user])

  if (!notification) return null

  const dismiss = () => {
    addDismissedId(notification.id)
    setNotification(null)
  }

  const handleView = async (targetUrl?: string) => {
    const { id, actionUrl } = notification
    addDismissedId(id)
    setNotification(null)
    try {
      await notificationsApi.markAsRead(id)
    } catch {
      // ignore -- getting the user to the action is more important than this call
    }
    const url = targetUrl ?? actionUrl
    if (url) navigate(url)
  }

  // RENTAL_APPROVED and FULFILLMENT_READY_FOR_PICKUP's actionUrl is always
  // `/rental/{id}` with no anchor (see rentalController.ts / fulfillmentRoutes.ts)
  // -- reused here with a hash so the shortcut jumps straight to the relevant
  // card (Payment summary / Pickup Location) instead of just the top of the page.
  //
  // PAYMENT_RECEIVED is different: paymentController.ts's markRentalPaid already
  // resolves the owner's actual next action server-side and points actionUrl
  // straight at it -- `/fulfillment/{id}/shipping` (Track Shipment) for a
  // Shipping/DoorDash rental, `/rental/{id}` for Pickup. A Shipping owner used to
  // land on the plain rental page and have no idea a separate Track Shipment page
  // even existed. Only append the Pickup-specific hash/label when actionUrl is
  // still the generic rental URL -- appending it to an already-specific
  // `/fulfillment/...` URL would just be a broken anchor with a misleading label.
  const SHORTCUTS: Record<string, { label: string; hash: string; color: string }> = {
    RENTAL_APPROVED: { label: 'Pay now', hash: '#payment-summary', color: '#F7A82F' },
    PAYMENT_RECEIVED: { label: 'Mark ready for pickup', hash: '#pickup-location', color: '#F7A82F' },
    FULFILLMENT_READY_FOR_PICKUP: { label: 'Track pickup', hash: '#pickup-location', color: '#F7A82F' },
  }
  const shortcut = SHORTCUTS[notification.type]
  const shortcutUrl = shortcut && notification.actionUrl && !notification.actionUrl.includes('/fulfillment/')
    ? `${notification.actionUrl}${shortcut.hash}`
    : null

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 'calc(var(--navbar-height) + 0.75rem)',
        right: '1rem',
        left: '1rem',
        maxWidth: '380px',
        marginLeft: 'auto',
        zIndex: 1002,
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
        padding: '1rem',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'flex-start',
      }}
    >
      <span
        className="mdi mdi-bell-ring-outline"
        style={{ fontSize: '1.25rem', color: 'var(--forest-green)', flexShrink: 0, marginTop: '0.125rem' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '2px' }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            {notification.title}
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            style={{
              flexShrink: 0,
              border: 'none',
              background: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
              padding: '0.125rem',
              marginTop: '-0.125rem',
              marginRight: '-0.25rem',
            }}
          >
            <span className="mdi mdi-close" />
          </button>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          {notification.message}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {shortcut && shortcutUrl && (
            <button
              type="button"
              onClick={() => handleView(shortcutUrl)}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '8px',
                border: 'none',
                background: shortcut.color,
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              {shortcut.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => handleView()}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--forest-green)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            View
          </button>
          <button
            type="button"
            onClick={dismiss}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
